import { inject } from '@angular/core';
import { CanMatchFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ToastController } from '@ionic/angular';
import { Observable, of, switchMap, take } from 'rxjs';

import {
  fetchModulesStatus,
  fetchModulesStatusFailed,
  fetchModulesStatusSuccess,
} from '@lessor/core/store/subscription/subscription.actions';
import {
  selectModulesStatus,
  selectModulesStatusLastFetched,
} from '@lessor/core/store/subscription/subscription.selectors';

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Variante de `moduleGuard` como `CanMatchFn`.
 *
 * Diferença crítica para o `moduleGuard` (que é `CanActivateFn`): o `canActivate`
 * roda DEPOIS que o chunk lazy já foi baixado — ele só bloqueia a ativação. Já o
 * `canMatch` roda DURANTE o casamento da rota, ANTES do `loadChildren` disparar o
 * download. Usar este guard num `loadChildren` garante que o bundle do módulo só
 * é baixado quando o CE realmente tem o módulo ativo na assinatura.
 *
 * Sem entitlement, em vez de deixar a rota sem match (navegação fica "presa"),
 * redireciona pra loja de módulos (`/subscription`) e avisa via toast.
 *
 * Fail-open: se a busca do status de módulos falhar, libera o match (mesmo
 * comportamento do `moduleGuard`) — não bloqueamos o CE por erro de rede.
 */
export function moduleMatchGuard(moduleKey: string): CanMatchFn {
  return () => {
    const store = inject(Store);
    const router = inject(Router);
    const actions$ = inject(Actions);
    const toastController = inject(ToastController);

    const redirectToStore = (moduleName: string): UrlTree => {
      showModuleInactiveToast(toastController, moduleName);
      return router.parseUrl('/subscription');
    };

    const lastFetched = store.selectSignal(selectModulesStatusLastFetched)();
    const now = Date.now();
    const isCacheValid = lastFetched && now - lastFetched < CACHE_DURATION_MS;

    if (isCacheValid) {
      const mod = store
        .selectSignal(selectModulesStatus)()
        .find((m) => m.key === moduleKey);
      return mod?.active ? true : redirectToStore(mod?.name ?? moduleKey);
    }

    store.dispatch(fetchModulesStatus());

    return actions$.pipe(
      ofType(fetchModulesStatusSuccess, fetchModulesStatusFailed),
      take(1),
      switchMap((action): Observable<boolean | UrlTree> => {
        if (action.type === fetchModulesStatusFailed.type) {
          return of(true); // fail-open
        }
        const mod = store
          .selectSignal(selectModulesStatus)()
          .find((m) => m.key === moduleKey);
        return of(mod?.active ? true : redirectToStore(mod?.name ?? moduleKey));
      })
    );
  };
}

async function showModuleInactiveToast(
  toastController: ToastController,
  moduleName: string
): Promise<void> {
  const toast = await toastController.create({
    message: `O módulo "${moduleName}" não está ativo na sua assinatura. Contrate na loja de módulos.`,
    duration: 4000,
    position: 'bottom',
    color: 'warning',
  });
  await toast.present();
}
